/* eslint-disable no-unused-vars */
import React, { FC, memo, useMemo, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  FormControlLabel,
  LinearProgress,
  Paper,
  Switch,
  Typography
} from '@material-ui/core';
import { Pagination } from '@material-ui/lab';
import { MobileTable } from 'src/components';
import useCustomerPayment from 'src/hooks/customer-payment/use-customer-payment';
import { MOPCell } from './MOPCell';
import { AmountCell } from './AmountCell';
import { useNavigate } from 'react-router';
import { cleanUrlParams } from 'src/utils';
import { CommonCustomerPaymentPayload } from 'src/redux/slices/customer-payment';
import useResolution from 'src/hooks/useResolution';

const headers = [
  'Date Sold',
  'Payment Date',
  'Branch',
  'Customer',
  'SI No.',
  'MOP',
  'Amount'
];

interface Props {}

const Component: FC<Props> = () => {
  const navigate = useNavigate();
  const paperRef = useRef<HTMLDivElement>(null);
  const {
    customerPaymentList,
    customerPaymentParams,
    setParams,
    fetchList
  } = useCustomerPayment();
  const { xs, sm } = useResolution();

  const meta = customerPaymentList?.meta;

  const [showWithBalanceOnly, setShowWithBalanceOnly] = useState<boolean>(
    false
  );

  /** 🔵 FILTERING (Same logic as desktop) */
  const filteredList = useMemo(() => {
    if (showWithBalanceOnly) {
      return customerPaymentList?.list?.filter((data) =>
        data?.balance ? data?.balance > 0 : false
      );
    }
    return customerPaymentList?.list;
  }, [customerPaymentList.list, showWithBalanceOnly]);

  /** 🔵 PAGINATION HANDLER (Same logic as desktop) */
  const onHandlePageChange = (params: CommonCustomerPaymentPayload) => {
    const cleanParams = cleanUrlParams(params);
    setParams(params);
    fetchList(params);
    navigate(`?${cleanParams.toString()}`);

    // scroll to top
    setTimeout(() => {
      paperRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 0);
  };

  return (
    <Paper ref={paperRef}>
      {customerPaymentList?.is_loading ? <LinearProgress /> : null}

      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          flexDirection: xs || sm ? 'column' : 'row',
          padding: 15
        }}
      >
        <FormControlLabel
          control={
            <Switch
              checked={showWithBalanceOnly}
              onChange={(e) => {
                setShowWithBalanceOnly(e.target.checked);
              }}
              color="primary"
            />
          }
          label="Show Payments With Balance Only"
        />

        <div
          style={{
            marginTop: xs || sm ? 15 : 0,
            display: 'flex',
            justifyContent: xs || sm ? 'center' : 'flex-end'
          }}
        >
          <Pagination
            disabled={customerPaymentList?.is_loading}
            count={meta?.last_page || 1}
            page={Number(customerPaymentParams?.page) || 1}
            onChange={(e, p) =>
              onHandlePageChange({ ...customerPaymentParams, page: p })
            }
          />
        </div>
      </Box>
      {filteredList?.length > 0 ? (
        <>
          {filteredList?.map((payment, index) => (
            <MobileTable
              key={`${payment?.transaction_no}-${index}`}
              index={index}
              rows={headers}
              renderMap={{
                'Date Sold': <Typography>{payment?.date_sold}</Typography>,
                'Payment Date': (
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    {payment?.payments?.map((p, idx) => (
                      <Typography key={idx}>{p?.payment_date}</Typography>
                    ))}
                  </div>
                ),
                Branch: <Typography>{payment?.branch}</Typography>,
                Customer: <Typography>{payment?.customer}</Typography>,
                'SI No.': <Typography>{payment?.si_no}</Typography>,
                MOP: <MOPCell data={payment?.payments || []} />,
                Amount: <AmountCell data={payment} />
              }}
            />
          ))}
        </>
      ) : (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '40vh'
          }}
        >
          <Typography>--- Nothing to show ---</Typography>
        </div>
      )}

      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: xs || sm ? 'center' : 'flex-end',
          padding: 15
        }}
      >
        <Pagination
          disabled={customerPaymentList?.is_loading}
          count={meta?.last_page || 1}
          page={Number(customerPaymentParams?.page) || 1}
          onChange={(e, p) =>
            onHandlePageChange({ ...customerPaymentParams, page: p })
          }
        />
      </Box>
    </Paper>
  );
};

export const MobileTablePayments = memo(Component);
